import { apiClient } from '@/lib/api/client'
import { env } from '@/config/env'
import type { Sale } from '@/types/entities'
import type { PaginationParams, PaginatedResponse } from '@/types/common'

export const salesApi = {
  // Get sales with pagination
  getSales: async (pagination: PaginationParams): Promise<PaginatedResponse<Sale>> => {
    const response = await apiClient.get<Sale[] & { count?: number }>('/api/sales', {
      params: {
        start: pagination.start,
        limit: pagination.limit,
      },
    })
    
    // The row count arrives as an HTTP HEADER, not a property on the array.
    //
    // This read `sales?.count`, which a JSON array parsed from a response body can never have —
    // so `total` was 0 for everyone, forever, and the Sales screen showed "Total Sales 0" above a
    // table of real orders. Käthe has 1,532.
    const sales = response.data
    const headerCount = Number(response.headers?.count ?? response.headers?.['count'])

    // `commission` and `productPrice` are Postgres `numeric`, which node-postgres returns as
    // STRINGS to preserve precision. `0 + '8.500'` is not 8.5, it is the string "08.5", and
    // formatCurrency of that is NaN — which is what the Total Commission card was showing.
    // Coerced here, at the boundary where the Sale type claims these are numbers.
    const rows = (sales || []).map((s) => ({
      ...s,
      commission: Number(s.commission) || 0,
      productPrice: Number(s.productPrice) || 0,
      productQuantity: Number(s.productQuantity) || 0,
    }))

    return {
      data: rows,
      total: Number.isFinite(headerCount) ? headerCount : rows.length,
      start: pagination.start,
      limit: pagination.limit,
    }
  },

  // Refresh sales from Shopify
  refreshSales: async (): Promise<void> => {
    await apiClient.get('/api/sales/refresh')
  },

  // Export sales to CSV
  exportSales: (from: string, to: string): string => {
    return `${env.apiUrl}/artwork-ms/api/sales/export?min=${from}&max=${to}`
  },
}

/** Units, revenue and commission for one artwork — the caller's own. */
export interface ArtworkSales {
  artworkId: number
  units: number
  revenue: number
  commission: number
  lastSold: string | null
}

/**
 * Sales rolled up per artwork, keyed by artwork id.
 *
 * A Map rather than an array because every caller wants to look one up while rendering a row of
 * the inventory table, and an artwork with no sales is a legitimate miss rather than a gap.
 */
export const salesByArtwork = async (): Promise<Map<number, ArtworkSales>> => {
  const res = await apiClient.get<{ data: ArtworkSales[] }>('/api/sales/by-artwork')
  return new Map((res.data.data ?? []).map((r) => [r.artworkId, r]))
}
